import { inspect } from 'node:util';

import { LogPayload, LogWriter, MessageOrError } from './logger.types';
import { GCPLogWriter } from '@/shared/helpers/logger/gcp-log-writer';
import { ConsoleLogWriter } from './console-log-writer';

// Next.js overrides Error.prepareStackTrace with a non-remapping function and exposes
// source-mapped stacks only through util.inspect (via Symbol.for('nodejs.util.inspect.custom')).
// Reading err.stack directly returns minified frames; util.inspect goes through Next's hook.
function getSourceMappedStack(error: Error): string {
  const hasCustomInspect =
    typeof (error as unknown as { [k: symbol]: unknown })[
      Symbol.for('nodejs.util.inspect.custom')
    ] === 'function';
  if (!hasCustomInspect) return error.stack ?? '';
  try {
    return inspect(error, { colors: false, depth: 4, breakLength: Infinity });
  } catch {
    return error.stack ?? '';
  }
}

export function serializeError(
  error: unknown,
  seen = new WeakSet<object>(),
): Record<string, unknown> | string {
  if (error === null || typeof error !== 'object') {
    return {};
  }

  if (seen.has(error)) return '[Circular]';
  seen.add(error);

  const serialized: Record<string, unknown> = {};

  for (const key of Reflect.ownKeys(error)) {
    const stringKey = String(key);

    try {
      const value = (error as Record<string | symbol, unknown>)[key];

      if (typeof value === 'object' && value !== null) {
        serialized[stringKey] = serializeError(value, seen);
      } else {
        serialized[stringKey] = value;
      }
    } catch (e) {
      serialized[stringKey] = `[Unserializable: ${e instanceof Error ? e.message : String(e)}]`;
    }
  }

  if (error instanceof Error) {
    if (!('name' in serialized)) {
      serialized.name = error.name;
    }

    if (!('message' in serialized)) {
      serialized.message = error.message;
    }

    serialized.stack = getSourceMappedStack(error);
  }

  return serialized;
}

export class Logger {
  private static writer: LogWriter = Logger.detectDefaultWriter();

  static setWriter(writer: LogWriter) {
    Logger.writer = writer;
  }

  public static debug(msg: MessageOrError, meta?: Record<string, unknown>) {
    return this.log({ messageOrError: msg, metadata: meta, severity: 'DEBUG' });
  }

  public static info(msg: MessageOrError, meta?: Record<string, unknown>) {
    return this.log({ messageOrError: msg, metadata: meta, severity: 'INFO' });
  }

  public static warn(msg: MessageOrError, meta?: Record<string, unknown>) {
    return this.log({ messageOrError: msg, metadata: meta, severity: 'WARNING' });
  }

  public static error(msg: MessageOrError, meta?: Record<string, unknown>) {
    return this.log({ messageOrError: msg, metadata: meta, severity: 'ERROR' });
  }

  public static critical(msg: MessageOrError, meta?: Record<string, unknown>) {
    return this.log({ messageOrError: msg, metadata: meta, severity: 'CRITICAL' });
  }

  public static log({ messageOrError, metadata = {}, severity = 'INFO' }: LogPayload) {
    const { message, structuredMeta } = this.parseMessage(messageOrError, metadata);
    try {
      Logger.writer.write(severity, message, structuredMeta);
    } catch (err) {
      console.error('[Logger] Logging failed:', err);
    }
  }

  private static detectDefaultWriter(): LogWriter {
    if (process.env.K_SERVICE) {
      return new GCPLogWriter();
    }
    return new ConsoleLogWriter();
  }

  private static parseMessage(messageOrError: MessageOrError, metadata: Record<string, unknown>) {
    const structuredMeta = { ...metadata };
    let message: string;

    if (messageOrError instanceof Error) {
      message = messageOrError.message;
      structuredMeta.error = serializeError(messageOrError);
    } else if (typeof messageOrError === 'object') {
      try {
        message = JSON.stringify(messageOrError);
      } catch {
        message = '[Unserializable object]';
      }
    } else {
      message = String(messageOrError);
      if ('error' in structuredMeta) {
        structuredMeta.error = serializeError(structuredMeta.error);
      }
    }

    return { message, structuredMeta };
  }
}
